// autopilot-resolve-deploy-command.ts
//
// Close-out A5 (Luke Q4, 2026-09-30): `avatar autopilot resolve-deploy
// --candidate <sha> --outcome deployed|not-deployed [--mission <id>]
// [--repo <path>] [--json]` — the exit from `blocked:deploy_outcome_unknown`
// other than `archive`: a person who checked the deploy target records what
// they found, for one named candidate. Logic lives in
// release-deploy-outcome-manual-resolution.ts; registration mirrors
// autopilot-archive-command.ts.
import type { Command } from "commander";
import { resolveCliActorId } from "../lib/autopilot/autopilot-control-directive-shared.js";
import { loadEffectiveConfig } from "../lib/autopilot/effective-config-loader.js";
import { MissionResolutionError, resolveMission } from "../lib/autopilot/mission-resolver.js";
import { realLivenessProbe } from "../lib/autopilot/process-liveness-probe.js";
import {
  type ManualDeployAnswer,
  type ResolveUnknownDeployResult,
  resolveUnknownDeployOutcome,
} from "../lib/autopilot/release-deploy-outcome-manual-resolution.js";
import { computeReleaseDeployHash } from "../lib/autopilot/release-manifest-hash.js";
import { writeMachineReadableJson } from "../lib/machine-readable-json-output-contract.js";
import { log } from "../lib/terminal-logger.js";

export interface RegisterAutopilotResolveDeployCommandDeps {
  resolveWorkspaceRoot: (repoFlag: string | undefined) => Promise<string>;
  machineLockPath: string;
  /** Global autopilot config — `deployed` must match the CURRENT config.release (review P2 H2). */
  globalConfigPath: string;
}

/** Deploy hash of the current config.release for `candidate`, `undefined` when it cannot be loaded. */
async function currentDeployHash(
  globalConfigPath: string,
  controlRoot: string,
  candidate: string,
): Promise<string | undefined> {
  const loaded = await loadEffectiveConfig({ globalConfigPath, controlRoot });
  if (!loaded.ok || loaded.config.release === undefined) return undefined;
  return computeReleaseDeployHash(candidate.toLowerCase(), loaded.config.release);
}

const ANSWERS: readonly ManualDeployAnswer[] = ["deployed", "not-deployed"];

interface ResolveDeployOpts {
  candidate: string;
  outcome: string;
  mission?: string;
  repo?: string;
  json?: boolean;
}

/**
 * What the operator is told after an ok answer. The sentence about the next
 * `run` follows the deploy receipt that really decides it (review 261001 M2):
 * a receipt a real deploy wrote is kept and a damaged one is never guessed at,
 * so neither lets the next `run` deploy again.
 */
function describeResolutionOutcome(
  result: Extract<ResolveUnknownDeployResult, { ok: true }>,
): string[] {
  const receiptFile = `release-state/deploy-${result.candidate_sha}.json`;
  if (result.outcome === "deployed") {
    return [
      "  `avatar autopilot run` kế tiếp ghi receipt deploy rồi CHỈ chạy smoke (không deploy lại).",
    ];
  }
  const redeploy =
    "  `avatar autopilot run` kế tiếp được deploy lại nếu G3 còn hiệu lực và worktree sạch.";
  if (!result.retracted_manual_deployed) return [redeploy];
  if (result.blocking_deploy_receipt === "real") {
    return [
      "  Đã rút lại câu trả lời `deployed` trong journal, NHƯNG receipt deploy hiện có do một deploy thật ghi " +
        "(không phải câu trả lời tay) nên được giữ nguyên.",
      "  `avatar autopilot run` kế tiếp KHÔNG deploy lại — chỉ chạy smoke. Kiểm tay môi trường đích; " +
        "bỏ mission ⇒ `avatar autopilot archive`.",
    ];
  }
  if (result.blocking_deploy_receipt === "damaged") {
    return [
      `  Đã rút lại câu trả lời \`deployed\` trong journal, NHƯNG receipt ${receiptFile} hỏng nên được giữ nguyên.`,
      "  `avatar autopilot run` kế tiếp dừng ở `blocked:deploy_outcome_unknown` vì receipt hỏng: xoá (hoặc dời) " +
        "đúng file đó rồi `run` lại, hoặc `avatar autopilot archive`.",
    ];
  }
  return [
    result.deploy_receipt_removed
      ? "  Đã rút lại câu trả lời `deployed` trước đó; receipt deploy dựng từ nó đã xoá."
      : "  Đã rút lại câu trả lời `deployed` trước đó (chưa có receipt dựng từ nó).",
    redeploy,
  ];
}

export function registerAutopilotResolveDeployCommand(
  autopilot: Command,
  deps: RegisterAutopilotResolveDeployCommandDeps,
): void {
  autopilot
    .command("resolve-deploy")
    .description("Ghi kết quả deploy đã KIỂM TAY cho mission dừng ở blocked:deploy_outcome_unknown")
    .requiredOption("--candidate <sha>", "SHA đầy đủ của candidate đã kiểm trên môi trường đích")
    .requiredOption("--outcome <answer>", "deployed | not-deployed")
    .option("--mission <id>", "Mission ID")
    .option("--repo <path>", "Repo path để resolve mission")
    .option("--json", "Output JSON")
    .action(async (opts: ResolveDeployOpts) => {
      if (!ANSWERS.includes(opts.outcome as ManualDeployAnswer)) {
        const message = `--outcome phải là deployed hoặc not-deployed, nhận "${opts.outcome}".`;
        if (opts.json) {
          writeMachineReadableJson({
            command: "autopilot resolve-deploy",
            ok: false,
            reason: "outcome_invalid",
            message,
          });
        } else log.error(message);
        process.exit(1);
      }
      const workspaceRoot = await deps.resolveWorkspaceRoot(opts.repo);
      let resolved: Awaited<ReturnType<typeof resolveMission>>;
      try {
        resolved = await resolveMission({ workspaceRoot, explicitMissionId: opts.mission });
      } catch (err) {
        if (!(err instanceof MissionResolutionError)) throw err;
        if (opts.json) {
          writeMachineReadableJson({
            command: "autopilot resolve-deploy",
            ok: false,
            reason: "mission_not_resolved",
            message: err.message,
          });
        } else log.error(err.message);
        process.exit(1);
      }
      const result = await resolveUnknownDeployOutcome({
        currentDeployHash: await currentDeployHash(
          deps.globalConfigPath,
          resolved.controlRoot,
          opts.candidate,
        ),
        controlRoot: resolved.controlRoot,
        missionId: resolved.missionId,
        candidateSha: opts.candidate,
        outcome: opts.outcome as ManualDeployAnswer,
        actorId: resolveCliActorId(),
        lockPath: deps.machineLockPath,
        liveness: realLivenessProbe,
      });

      if (opts.json) {
        writeMachineReadableJson({ command: "autopilot resolve-deploy", ...result });
      } else if (result.ok) {
        log.info(
          `resolve-deploy: mission ${result.mission_id} · candidate ${result.candidate_sha} · ${result.outcome}`,
        );
        for (const line of describeResolutionOutcome(result)) log.info(line);
      } else {
        log.error(result.message);
      }
      if (!result.ok) process.exit(1);
    });
}
